import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Spinner } from '../components/ui';

const CERT_LABELS = {
  epc:'EPC', gas_safety:'Gas Safety', eicr:'EICR',
  smoke_alarm:'Smoke Alarm', co_alarm:'CO Alarm', legionella:'Legionella',
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  let day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Monday = 0
}

export default function Calendar() {
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const h = () => { const ac = localStorage.getItem('lc_access_code'); return { Authorization:`Bearer ${localStorage.getItem('token')}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  useEffect(() => {
    async function load() {
      const [props, tasks, rents] = await Promise.all([
        fetch('/api/properties', { headers: h() }).then(r => r.json()),
        fetch('/api/tasks',      { headers: h() }).then(r => r.json()),
        fetch('/api/rent-payments', { headers: h() }).then(r => r.json()),
      ]);

      const ps = Array.isArray(props) ? props : [];
      const allCerts = [];

      await Promise.all(ps.map(async p => {
        const c = await fetch(`/api/properties/${p.id}/certificates`, { headers: h() }).then(r => r.json());
        (Array.isArray(c) ? c : []).forEach(cert => {
          if (cert.expiry_date) allCerts.push({
            id: cert.id, date: cert.expiry_date,
            label: `${CERT_LABELS[cert.cert_type] || cert.cert_type} expires`,
            sublabel: p.address_line1,
            type: 'cert',
            color: cert.status === 'expired' ? '#dc2626' : cert.status === 'due_soon' ? '#d97706' : '#3b82f6',
            link: '/properties',
          });
        });
      }));

      const taskEvents = (Array.isArray(tasks) ? tasks : [])
        .filter(t => t.due_date)
        .map(t => ({
          id: t.id, date: t.due_date,
          label: t.title, sublabel: t.property_address || '',
          type: 'task',
          color: t.priority === 'high' ? '#dc2626' : t.priority === 'medium' ? '#d97706' : '#16a34a',
          link: '/tasks',
        }));

      // Generate rent due events from property rent_due_day settings
      const rentEvents = [];
      const rentPayments = Array.isArray(rents) ? rents : [];

      ps.filter(p => p.rent_due_day && p.rent_amount_gbp > 0).forEach(p => {
        [-1, 0, 1, 2].forEach(monthOffset => {
          const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, p.rent_due_day);
          const dateStr = d.toISOString().split('T')[0];
          const monthStart = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
          const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];

          const paid = rentPayments.find(r =>
            r.property_id === p.id &&
            r.status === 'paid' &&
            r.due_date >= monthStart &&
            r.due_date <= monthEnd
          );

          const isPast = d < today;
          rentEvents.push({
            id: `rent-due-${p.id}-${dateStr}`,
            date: dateStr,
            label: paid ? `✓ Rent paid — ${p.address_line1}` : `Rent due — ${p.address_line1}`,
            sublabel: `£${(p.rent_amount_gbp / 100).toLocaleString()}/mo`,
            type: 'rent',
            color: paid ? '#16a34a' : isPast ? '#dc2626' : '#8b5cf6',
            link: '/rent',
          });
        });
      });

      // Also show any logged unpaid payments for properties without rent_due_day
      rentPayments.filter(r => r.status !== 'paid').forEach(r => {
        if (!ps.find(p => p.id === r.property_id && p.rent_due_day)) {
          rentEvents.push({
            id: r.id, date: r.due_date,
            label: `Rent due — ${r.property_address || ''}`,
            sublabel: `£${(r.amount_gbp/100).toFixed(0)}`,
            type: 'rent',
            color: r.status === 'late' ? '#dc2626' : '#8b5cf6',
            link: '/rent',
          });
        }
      });

      setEvents([...allCerts, ...taskEvents, ...rentEvents]);
      setLoading(false);
    }
    load();
  }, []);

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  const daysInMonth  = getDaysInMonth(viewYear, viewMonth);
  const firstDayIdx  = getFirstDayOfMonth(viewYear, viewMonth);

  // Build event map for this month
  const eventMap = {};
  events.forEach(e => {
    const d = new Date(e.date);
    if (d.getFullYear() === viewYear && d.getMonth() === viewMonth) {
      const day = d.getDate();
      if (!eventMap[day]) eventMap[day] = [];
      eventMap[day].push(e);
    }
  });

  // Upcoming events list (next 60 days)
  const upcoming = events
    .filter(e => {
      const d = new Date(e.date);
      const diff = Math.ceil((d - today) / 86400000);
      return diff >= 0 && diff <= 60;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 10);

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ marginBottom:32 }}>
        <h1 style={{ fontSize:32, fontWeight:300 }}>Compliance <em>Calendar</em></h1>
        <p style={{ color:'var(--ink-3)', marginTop:4 }}>Certificate expiries, task due dates and rent payments</p>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:24 }}>
        {/* Calendar grid */}
        <Card style={{ padding:24 }}>
          {/* Nav */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:20 }}>
            <button onClick={prevMonth} style={{ background:'none', border:'1.5px solid var(--border)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:16 }}>←</button>
            <h2 style={{ fontSize:20, fontWeight:300 }}>{MONTHS[viewMonth]} {viewYear}</h2>
            <button onClick={nextMonth} style={{ background:'none', border:'1.5px solid var(--border)', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:16 }}>→</button>
          </div>

          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:2, marginBottom:4 }}>
            {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:12, fontWeight:600, color:'var(--ink-3)', padding:'4px 0' }}>{d}</div>)}
          </div>

          {/* Day cells */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:2 }}>
            {/* Empty cells before first day */}
            {Array.from({ length:firstDayIdx }).map((_, i) => <div key={`e${i}`} />)}

            {/* Days */}
            {Array.from({ length:daysInMonth }, (_, i) => i + 1).map(day => {
              const isToday = today.getDate() === day && today.getMonth() === viewMonth && today.getFullYear() === viewYear;
              const dayEvents = eventMap[day] || [];
              return (
                <div key={day} style={{
                  minHeight:64, padding:'6px 4px', borderRadius:6,
                  background: isToday ? 'var(--accent-lt, #f0fdf4)' : 'transparent',
                  border: isToday ? '1.5px solid var(--accent, #16a34a)' : '1px solid transparent',
                }}>
                  <p style={{ fontSize:13, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--accent)' : 'var(--ink)', marginBottom:3 }}>{day}</p>
                  {dayEvents.slice(0, 3).map((e, i) => (
                    <Link key={i} to={e.link} style={{ textDecoration:'none' }}>
                      <div style={{
                        fontSize:10, fontWeight:600, color:'#fff',
                        background: e.color, borderRadius:3,
                        padding:'2px 4px', marginBottom:2,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                      }} title={e.label}>
                        {e.label}
                      </div>
                    </Link>
                  ))}
                  {dayEvents.length > 3 && (
                    <p style={{ fontSize:10, color:'var(--ink-3)' }}>+{dayEvents.length - 3} more</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display:'flex', gap:16, marginTop:16, flexWrap:'wrap' }}>
            {[
              { color:'#3b82f6', label:'Certificate expiry' },
              { color:'#dc2626', label:'Expired / High priority' },
              { color:'#d97706', label:'Due soon / Medium priority' },
              { color:'#8b5cf6', label:'Rent due' },
              { color:'#16a34a', label:'Low priority task' },
            ].map(l => (
              <div key={l.label} style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:12, height:12, borderRadius:3, background:l.color }} />
                <span style={{ fontSize:11, color:'var(--ink-3)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Upcoming events sidebar */}
        <div>
          <h3 style={{ fontSize:16, marginBottom:12 }}>Next 60 days</h3>
          {upcoming.length === 0 ? (
            <Card style={{ padding:20, textAlign:'center' }}>
              <p style={{ color:'var(--ink-3)', fontSize:14 }}>Nothing coming up 🎉</p>
            </Card>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {upcoming.map(e => {
                const days = Math.ceil((new Date(e.date) - today) / 86400000);
                return (
                  <Link key={`${e.type}-${e.id}`} to={e.link} style={{ textDecoration:'none' }}>
                    <Card style={{ padding:'12px 14px', cursor:'pointer' }}>
                      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                        <div style={{ width:4, alignSelf:'stretch', borderRadius:2, background:e.color, flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:13, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.label}</p>
                          {e.sublabel && <p style={{ fontSize:11, color:'var(--ink-3)' }}>{e.sublabel}</p>}
                          <p style={{ fontSize:11, color: days <= 7 ? '#dc2626' : days <= 30 ? '#d97706' : 'var(--ink-3)', fontWeight:600, marginTop:2 }}>
                            {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}